import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { IS_NATIVE, NATIVE_APP_ENV } from '../native/platform.js';

/**
 * Bandeau d'environnement — affiché en haut de l'écran hors production pour
 * éviter toute confusion entre staging/dev et la prod (PII réelles en staging).
 * Détection 100 % runtime par hostname : aucun rebuild ni variable d'env requis.
 * App native : la WebView tourne sur `localhost` quel que soit l'environnement,
 * on se fie donc à `VITE_APP_ENV` (fixé par le fichier `.env.native-*`).
 */
function detectEnv() {
  if (IS_NATIVE) {
    return NATIVE_APP_ENV === 'production'
      ? { label: 'PROD', show: false }
      : { label: String(NATIVE_APP_ENV).toUpperCase(), show: true };
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return { label: 'DEV', show: true };
  }
  if (host.includes('staging')) {
    return { label: 'STAGING', show: true };
  }
  return { label: 'PROD', show: false };
}

const BANNER_HEIGHT = 22;
const NATIVE_BANNER_TOTAL = `calc(${BANNER_HEIGHT}px + env(safe-area-inset-top))`;

export default function EnvBanner() {
  const env = detectEnv();

  // Décale tout le contenu vers le bas pour que le bandeau ne masque pas le
  // header sticky de l'app. Nettoyé si le bandeau disparaît.
  useEffect(() => {
    if (!env.show) return undefined;
    const prev = document.body.style.paddingTop;
    if (IS_NATIVE) {
      // Natif (viewport-fit=cover) : le bandeau occupe aussi la zone de
      // l'encoche. Le shell mobile lit ces variables pour ne pas compter la
      // safe area deux fois et pour réduire sa hauteur d'autant.
      const root = document.documentElement.style;
      document.body.style.paddingTop = NATIVE_BANNER_TOTAL;
      root.setProperty('--env-banner-h', NATIVE_BANNER_TOTAL);
      root.setProperty('--m-safe-top', '0px');
      return () => {
        document.body.style.paddingTop = prev;
        root.removeProperty('--env-banner-h');
        root.removeProperty('--m-safe-top');
      };
    }
    document.body.style.paddingTop = `${BANNER_HEIGHT}px`;
    return () => {
      document.body.style.paddingTop = prev;
    };
  }, [env.show]);

  if (!env.show) return null;

  return ReactDOM.createPortal(
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        height: IS_NATIVE ? NATIVE_BANNER_TOTAL : BANNER_HEIGHT,
        ...(IS_NATIVE ? { paddingTop: 'env(safe-area-inset-top)', boxSizing: 'border-box' } : {}),
        lineHeight: `${BANNER_HEIGHT}px`,
        textAlign: 'center',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 2,
        color: '#1a1a1a',
        background: 'var(--warning)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
      }}
    >
      {env.label}
    </div>,
    document.body,
  );
}
